// frontend/src/components/StaffHistoryModal.jsx
// スタッフ過去30日配置履歴モーダル

import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, UserCheck, RefreshCw, Upload, AlertCircle, CheckCircle, GraduationCap, User, Users } from 'lucide-react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import { writeClientAudit } from '../utils/auditClient';
import Papa from 'papaparse';
import dayjs from 'dayjs';

const ROLE_LABEL = { scrub: '器械出し', circulating: '外回り' };
const ROLE_COLOR = {
  scrub: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' },
  circulating: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200', dot: 'bg-teal-400' },
};

// pairing_mode のラベルと色
const PAIRING_LABEL = {
  instructor: '指導者として',
  trainee: '被指導者として',
  solo: '1人で（独立）',
};
const PAIRING_STYLE = {
  instructor: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', Icon: GraduationCap },
  trainee:    { bg: 'bg-sky-50',    text: 'text-sky-700',    border: 'border-sky-200',    Icon: Users },
  solo:       { bg: 'bg-slate-50',  text: 'text-slate-600',  border: 'border-slate-200',  Icon: User },
};

// pairing_mode 略称（CSV)
const PAIRING_CSV_MAP = {
  // ja
  '指導': 'instructor', '指導者': 'instructor', '指導者として': 'instructor',
  '被指導': 'trainee', '被指導者': 'trainee', '被指導者として': 'trainee', '育成対象': 'trainee',
  '独立': 'solo', '1人': 'solo', '一人': 'solo', 'ソロ': 'solo',
  // en
  'instructor': 'instructor', 'trainer': 'instructor', 'teacher': 'instructor',
  'trainee': 'trainee', 'student': 'trainee', 'learner': 'trainee',
  'solo': 'solo', 'alone': 'solo', 'single': 'solo',
};
const normalizePairingMode = (raw) => {
  if (!raw) return null;
  const k = String(raw).trim().toLowerCase();
  if (!k) return null;
  return PAIRING_CSV_MAP[k] || PAIRING_CSV_MAP[raw.trim()] || null;
};

// ==========================================
// CSVインポートモーダル（サブコンポーネント）
// ==========================================
const CsvImportModal = ({ onClose, onSuccess }) => {
  const { currentOrganization } = useOrganization();
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({
    date: '', employee_id: '', role: '', procedure_name: '', department_name: '',
    pairing_mode: '', paired_with_employee_id: '',
    level_at_assignment: '', level_after_assignment: '', note: '',
  });
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState('');

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const h = res.meta.fields || [];
        setHeaders(h);
        setPreview(res.data.slice(0, 5));
        const autoMap = {
          date: '', employee_id: '', role: '', procedure_name: '', department_name: '',
          pairing_mode: '', paired_with_employee_id: '',
          level_at_assignment: '', level_after_assignment: '', note: '',
        };
        h.forEach(col => {
          const c = col.toLowerCase();
          if (!autoMap.date && (c.includes('date') || c.includes('日付') || c.includes('配置日'))) autoMap.date = col;
          if (!autoMap.employee_id && (c.includes('employee') || c.includes('職員') || c === 'id' || c.includes('番号'))) autoMap.employee_id = col;
          if (!autoMap.role && (c.includes('role') || c.includes('役割') || c.includes('担当'))) autoMap.role = col;
          if (!autoMap.procedure_name && (c.includes('procedure') || c.includes('術式') || c.includes('手術'))) autoMap.procedure_name = col;
          if (!autoMap.department_name && (c.includes('department') || c.includes('診療科') || c.includes('科'))) autoMap.department_name = col;
          if (!autoMap.pairing_mode && (c.includes('pairing') || c.includes('指導役割') || c.includes('指導関係') || c.includes('教育'))) autoMap.pairing_mode = col;
          if (!autoMap.paired_with_employee_id && (c.includes('paired') || c.includes('ペア') || c.includes('指導者番号') || c.includes('被指導者番号'))) autoMap.paired_with_employee_id = col;
          if (!autoMap.level_at_assignment && (c.includes('level_at') || c.includes('配置時') || c.includes('スキル前') || c.includes('before'))) autoMap.level_at_assignment = col;
          if (!autoMap.level_after_assignment && (c.includes('level_after') || c.includes('配置後') || c.includes('スキル後') || c.includes('after'))) autoMap.level_after_assignment = col;
          if (!autoMap.note && (c.includes('note') || c.includes('メモ') || c.includes('備考'))) autoMap.note = col;
        });
        setMapping(autoMap);
        e.target._allData = res.data;
      },
      error: () => alert('CSVの読み込みに失敗しました'),
    });
  };

  const handleImport = async () => {
    if (!mapping.date || !mapping.employee_id || !mapping.role) {
      alert('日付・職員番号・役割のマッピングは必須です');
      return;
    }
    setImporting(true);
    setResult(null);
    try {
      const file = fileInputRef.current.files[0];
      const allData = await new Promise((res, rej) => {
        Papa.parse(file, {
          header: true, skipEmptyLines: true,
          complete: r => res(r.data),
          error: rej,
        });
      });
      const { data: staffData } = await supabase
        .from('staff')
        .select('id, employee_id, last_name, first_name')
        .eq('organization_id', currentOrganization?.id);
      const empToId = {};
      const empToName = {};
      (staffData || []).forEach(s => {
        empToId[String(s.employee_id)] = s.id;
        empToName[String(s.employee_id)] = `${s.last_name || ''} ${s.first_name || ''}`.trim();
      });

      const rows = [];
      const errors = [];
      allData.forEach((row, i) => {
        const rawDate = row[mapping.date]?.trim();
        const rawEmpId = row[mapping.employee_id]?.trim();
        const rawRole = row[mapping.role]?.trim()?.toLowerCase();
        const procName = mapping.procedure_name ? row[mapping.procedure_name]?.trim() : '';
        const deptName = mapping.department_name ? row[mapping.department_name]?.trim() : '';
        const rawPairing = mapping.pairing_mode ? row[mapping.pairing_mode]?.trim() : '';
        const rawPairedEmp = mapping.paired_with_employee_id ? row[mapping.paired_with_employee_id]?.trim() : '';
        const lvlAt = mapping.level_at_assignment ? row[mapping.level_at_assignment]?.trim() : '';
        const lvlAfter = mapping.level_after_assignment ? row[mapping.level_after_assignment]?.trim() : '';
        const noteVal = mapping.note ? row[mapping.note]?.trim() : '';
        if (!rawDate || !rawEmpId || !rawRole) { errors.push(`行${i + 2}: 必須項目不足`); return; }
        const date = dayjs(rawDate);
        if (!date.isValid()) { errors.push(`行${i + 2}: 日付が無効 "${rawDate}"`); return; }
        let role = '';
        if (rawRole.includes('scrub') || rawRole.includes('器械') || rawRole.includes('外科')) role = 'scrub';
        else if (rawRole.includes('circ') || rawRole.includes('外回') || rawRole.includes('外周')) role = 'circulating';
        else { errors.push(`行${i + 2}: 役割が不明 "${rawRole}"（scrub/circulating または器械出し/外回り）`); return; }
        const staffId = empToId[String(rawEmpId)];
        if (!staffId) { errors.push(`行${i + 2}: 職員番号 "${rawEmpId}" が見つかりません`); return; }

        const pairingMode = normalizePairingMode(rawPairing);
        const pairedStaffId = rawPairedEmp ? empToId[String(rawPairedEmp)] : null;
        const pairedName = rawPairedEmp ? empToName[String(rawPairedEmp)] : null;

        rows.push({
          organization_id: currentOrganization?.id,
          staff_id: staffId,
          employee_id: rawEmpId,
          assignment_date: date.format('YYYY-MM-DD'),
          role,
          procedure_name: procName || null,
          department_name: deptName || null,
          pairing_mode: pairingMode,
          paired_with_staff_id: pairedStaffId || null,
          paired_with_employee_id: rawPairedEmp || null,
          paired_with_name: pairedName || null,
          level_at_assignment: lvlAt || null,
          level_after_assignment: lvlAfter || null,
          note: noteVal || null,
        });
      });

      let successCount = 0;
      const BATCH = 100;
      for (let i = 0; i < rows.length; i += BATCH) {
        const batch = rows.slice(i, i + BATCH);
        const { error } = await supabase.from('staff_assignments').insert(batch);
        if (error) { errors.push(`バッチ挿入エラー: ${error.message}`); }
        else { successCount += batch.length; }
      }
      setResult({ success: successCount, errors });
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'create',
        targetTable: 'staff_assignments',
        targetSummary: `過去配置 CSV インポート: 成功 ${successCount} / 失敗 ${errors.length}`,
        changes: { successCount, errorCount: errors.length, totalRows: rows.length },
        success: successCount > 0,
        errorMessage: errors.length ? errors.slice(0, 2).join(' / ') : null,
      });
      if (successCount > 0) onSuccess?.();
    } catch (e) {
      setResult({ success: 0, errors: [e.message] });
      await writeClientAudit({
        organizationId: currentOrganization?.id,
        actionType: 'create',
        targetTable: 'staff_assignments',
        targetSummary: `過去配置 CSV インポートで例外`,
        success: false,
        errorMessage: e.message,
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    // 🔧 z-[10000] でStaffSelectionModal(z-9999)より前面に
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[10000] overflow-y-auto">
      <div className="flex justify-center" style={{ padding: '60px 16px 80px' }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col">
          <div className="bg-teal-600 text-white px-5 py-4 rounded-t-xl flex justify-between items-center flex-shrink-0">
            <div>
              <h3 className="font-bold text-lg">過去配置データ CSVインポート</h3>
              <p className="text-teal-100 text-xs mt-0.5">過去の配置履歴を一括登録できます</p>
            </div>
            <button onClick={onClose} className="hover:bg-teal-700 rounded-full p-1.5 transition">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-blue-800 mb-1">📄 CSVフォーマット</p>
              <p className="text-xs text-blue-700 mb-2">必須3項目＋任意項目を含むCSVをアップロードしてください。指導役割・スキルレベル情報も登録できます。</p>
              <div className="grid grid-cols-2 gap-1 text-xs">
                {[
                  ['日付 *', 'date / 日付 / 配置日', '2026-02-10'],
                  ['職員番号 *', 'employee_id / 職員番号', '1001'],
                  ['役割 *', 'role / 役割', 'scrub or circulating\n（器械出し / 外回り）'],
                  ['術式名', 'procedure / 術式', '穿頭術'],
                  ['診療科', 'department / 診療科', '脳神経外科'],
                  ['指導役割', 'pairing_mode / 指導役割', '指導 / 被指導 / 独立\n(instructor/trainee/solo)'],
                  ['ペア相手番号', 'paired_with_employee_id', '1002'],
                  ['配置時スキル', 'level_at_assignment', '見学済み'],
                  ['配置後スキル', 'level_after_assignment', '経験1回'],
                  ['メモ', 'note / 備考', '初回経験'],
                ].map(([label, hint, ex]) => (
                  <div key={label} className="bg-white rounded border border-blue-100 p-2">
                    <div className="font-medium text-blue-800">{label}</div>
                    <div className="text-gray-500 text-xs">{hint}</div>
                    <div className="text-gray-400 text-xs mt-0.5 whitespace-pre-line">例: {ex}</div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  const csv = [
                    'date,employee_id,role,procedure_name,department_name,pairing_mode,paired_with_employee_id,level_at_assignment,level_after_assignment,note',
                    '2026-02-10,1001,scrub,穿頭術,脳神経外科,指導,1002,指導可,指導可,',
                    '2026-02-10,1002,scrub,穿頭術,脳神経外科,被指導,1001,見学済み,経験1回,初回経験',
                    '2026-02-11,1001,circulating,胆嚢摘出術,外科,独立,,独り立ち,独り立ち,',
                  ].join('\n') + '\n';
                  const bom = '\uFEFF';
                  const b = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
                  const a = document.createElement('a');
                  a.href = URL.createObjectURL(b);
                  a.download = '配置履歴_テンプレート.csv';
                  a.click();
                }}
                className="mt-2 text-xs text-blue-600 hover:underline"
              >
                📥 テンプレートをダウンロード
              </button>
            </div>

            <div>
              <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFile} className="hidden" />
              <button
                onClick={() => fileInputRef.current.click()}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg py-6 text-gray-500 hover:border-teal-400 hover:text-teal-600 transition flex flex-col items-center gap-2"
              >
                <Upload className="h-8 w-8" />
                <span className="text-sm font-medium">{fileName || 'CSVファイルを選択'}</span>
              </button>
            </div>

            {preview.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">プレビュー（先頭5行）</p>
                <div className="overflow-x-auto border rounded text-xs">
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>{headers.map(h => <th key={h} className="px-2 py-1.5 text-left font-medium text-gray-600">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.map((row, i) => (
                        <tr key={i} className="border-t">
                          {headers.map(h => <td key={h} className="px-2 py-1 text-gray-700">{row[h]}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {headers.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">カラムマッピング</p>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['date', '日付 *'],
                    ['employee_id', '職員番号 *'],
                    ['role', '役割 *'],
                    ['procedure_name', '術式名'],
                    ['department_name', '診療科'],
                    ['pairing_mode', '指導役割'],
                    ['paired_with_employee_id', 'ペア相手 職員番号'],
                    ['level_at_assignment', '配置時スキル'],
                    ['level_after_assignment', '配置後スキル'],
                    ['note', 'メモ'],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
                      <select
                        value={mapping[key]}
                        onChange={e => setMapping(prev => ({ ...prev, [key]: e.target.value }))}
                        className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 focus:ring-2 focus:ring-teal-400"
                      >
                        <option value="">-- 選択 --</option>
                        {headers.map(h => <option key={h} value={h}>{h}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result && (
              <div className={`rounded-lg p-3 border ${result.success > 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                {result.success > 0 && (
                  <div className="flex items-center gap-2 text-green-700 mb-1">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">{result.success}件を登録しました</span>
                  </div>
                )}
                {result.errors.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-red-700 mb-1">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">{result.errors.length}件のエラー</span>
                    </div>
                    <div className="text-xs text-red-600 max-h-24 overflow-y-auto space-y-0.5">
                      {result.errors.map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="border-t bg-gray-50 px-5 py-3 flex justify-end gap-3 flex-shrink-0 rounded-b-xl">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100">閉じる</button>
            <button
              onClick={handleImport}
              disabled={!headers.length || importing}
              className={`px-5 py-2 rounded-lg text-sm font-medium text-white transition ${headers.length && !importing ? 'bg-teal-600 hover:bg-teal-700' : 'bg-gray-300 cursor-not-allowed'}`}
            >
              {importing ? (
                <span className="flex items-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" />インポート中...</span>
              ) : 'インポート実行'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// ==========================================
// スタッフ履歴モーダル（メイン）
// ==========================================
const StaffHistoryModal = ({ staff, role, onClose, onSelect, isSelected = false }) => {
  const { currentOrganization } = useOrganization();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCsvImport, setShowCsvImport] = useState(false);

  const fiscalYear = new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1;
  const expYear = staff?.year ? fiscalYear - Number(staff.year) + 1 : null;

  // 背景スクロールロック
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (staff && currentOrganization?.id) fetchHistory();
  }, [staff, currentOrganization?.id]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const since = dayjs().subtract(60, 'day').format('YYYY-MM-DD');
      const { data, error } = await supabase
        .from('staff_assignments')
        .select('*')
        .eq('organization_id', currentOrganization?.id)
        .eq('staff_id', staff.id)
        .gte('assignment_date', since)
        .order('assignment_date', { ascending: false });
      if (error) throw error;
      setAssignments(data || []);
    } catch (e) {
      console.error('履歴取得エラー:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!staff) return null;

  const scrubCount = assignments.filter(a => a.role === 'scrub').length;
  const circCount = assignments.filter(a => a.role === 'circulating').length;

  return (
    <>
      {/* 🔧 z-[9999]、上下ナビバー分のpadding、モーダル内でスクロール */}
      <div className="fixed inset-0 bg-black bg-opacity-60 z-[9999] flex items-center justify-center" style={{ padding: '60px 16px 80px' }}>
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col" style={{ height: '100%', maxHeight: '100%', overflow: 'hidden' }}>

          {/* ヘッダー（固定） */}
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-5 py-4 rounded-t-xl flex-shrink-0">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-bold text-lg">{staff.name}</h3>
                <div className="flex items-center gap-3 mt-1 text-slate-300 text-sm">
                  {expYear && <span>経験{expYear}年目</span>}
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: role === 'scrub' ? 'rgba(251,191,36,0.2)' : 'rgba(52,211,153,0.2)',
                      color: role === 'scrub' ? '#fbbf24' : '#34d399',
                    }}
                  >
                    {staff.skillLevel} ({role === 'scrub' ? '器械出し' : '外回り'})
                  </span>
                </div>
              </div>
              <button onClick={onClose} className="hover:bg-slate-600 rounded-full p-1.5 transition">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex gap-3 mt-3">
              <div className="bg-slate-600 rounded-lg px-3 py-1.5 text-center">
                <div className="text-lg font-bold text-amber-300">{scrubCount}</div>
                <div className="text-xs text-slate-300">器械出し</div>
              </div>
              <div className="bg-slate-600 rounded-lg px-3 py-1.5 text-center">
                <div className="text-lg font-bold text-teal-300">{circCount}</div>
                <div className="text-xs text-slate-300">外回り</div>
              </div>
              <div className="bg-slate-600 rounded-lg px-3 py-1.5 text-center">
                <div className="text-lg font-bold text-white">{assignments.length}</div>
                <div className="text-xs text-slate-300">合計（30日）</div>
              </div>
            </div>
          </div>

          {/* コンテンツ（スクロール） */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-gray-500" /> 過去30日の配置履歴
              </h4>
              <button
                onClick={() => setShowCsvImport(true)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-50 border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-100 transition"
              >
                <Upload className="h-3.5 w-3.5" /> CSVで過去データ入力
              </button>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-500 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">読み込み中...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <Calendar className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">過去30日の配置データがありません</p>
                <p className="text-xs mt-1">CSVで過去データを入力できます</p>
              </div>
            ) : (
              <div className="space-y-2">
                {assignments.map((a, i) => {
                  const rc = ROLE_COLOR[a.role] || ROLE_COLOR.scrub;
                  const ps = a.pairing_mode ? PAIRING_STYLE[a.pairing_mode] : null;
                  const PairingIcon = ps?.Icon;
                  const levelBefore = a.level_at_assignment;
                  const levelAfter = a.level_after_assignment;
                  const hasLevelChange = levelBefore && levelAfter && levelBefore !== levelAfter;
                  return (
                    <div key={i} className={`rounded-lg border px-3 py-2.5 ${rc.bg} ${rc.border}`}>
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 w-14 text-center">
                          <div className="text-sm font-bold text-gray-700">{dayjs(a.assignment_date).format('M/D')}</div>
                          <div className="text-xs text-gray-400">{['日','月','火','水','木','金','土'][dayjs(a.assignment_date).day()]}</div>
                        </div>
                        <div className="w-px h-8 bg-gray-200 flex-shrink-0" />
                        <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full border ${rc.text} ${rc.border} bg-white`}>
                          {ROLE_LABEL[a.role]}
                        </span>
                        <div className="flex-1 min-w-0">
                          {a.procedure_name && <div className="text-sm font-medium text-gray-800 truncate">{a.procedure_name}</div>}
                          {a.department_name && <div className="text-xs text-gray-500 truncate">{a.department_name}</div>}
                          {!a.procedure_name && !a.department_name && <div className="text-xs text-gray-400">術式未記録</div>}
                        </div>
                      </div>

                      {/* 指導役割 / ペア相手 / スキル変遷 */}
                      {(ps || a.paired_with_name || levelBefore || levelAfter) && (
                        <div className="mt-2 pt-2 border-t border-white/60 flex flex-wrap items-center gap-2 text-xs">
                          {ps && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${ps.bg} ${ps.text} ${ps.border}`}>
                              <PairingIcon className="h-3 w-3" />
                              {PAIRING_LABEL[a.pairing_mode]}
                            </span>
                          )}
                          {a.paired_with_name && (
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              <Users className="h-3 w-3 text-gray-400" />
                              ペア: <span className="font-medium text-gray-700">{a.paired_with_name}</span>
                            </span>
                          )}
                          {(levelBefore || levelAfter) && (
                            <span className="inline-flex items-center gap-1 text-gray-600">
                              <GraduationCap className="h-3 w-3 text-gray-400" />
                              <span className={hasLevelChange ? 'line-through text-gray-400' : 'text-gray-700 font-medium'}>
                                {levelBefore || '—'}
                              </span>
                              {hasLevelChange && (
                                <>
                                  <span className="text-gray-400">→</span>
                                  <span className="font-semibold text-emerald-700">{levelAfter}</span>
                                </>
                              )}
                              {!hasLevelChange && levelAfter && !levelBefore && (
                                <span className="font-medium text-gray-700">（配置後: {levelAfter}）</span>
                              )}
                            </span>
                          )}
                          {a.evaluated_at && (
                            <span className="text-gray-400 text-[10px]">
                              評価記入: {dayjs(a.evaluated_at).format('M/D')}{a.evaluated_by ? ` / ${a.evaluated_by}` : ''}
                            </span>
                          )}
                          {a.note && (
                            <span className="text-gray-500 italic truncate max-w-full">📝 {a.note}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* フッター（固定） */}
          <div className="border-t bg-gray-50 px-5 py-3 flex justify-between items-center flex-shrink-0 rounded-b-xl">
            <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-100">
              戻る
            </button>
            {isSelected ? (
              <button
                onClick={() => { onSelect(staff); onClose(); }}
                className="px-6 py-2 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                選択を解除
              </button>
            ) : (
              <button
                onClick={() => { onSelect(staff); onClose(); }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
              >
                <UserCheck className="h-4 w-4" />
                この人を選択
              </button>
            )}
          </div>
        </div>
      </div>

      {showCsvImport && (
        <CsvImportModal
          onClose={() => setShowCsvImport(false)}
          onSuccess={() => { setShowCsvImport(false); fetchHistory(); }}
        />
      )}
    </>
  );
};

export default StaffHistoryModal;